/**
 * 进度跟踪模块 - API服务
 * Progress Tracking Module - API Services
 * 
 * 注意：已移除合同(Contract)服务，所有数据直接与项目(Project)关联
 */
import api from '../../../services/api';
import type { 
  IWorkItem, 
  IProgressRecord, 
  IAddition, 
  IAdditionBatch,
  IAdditionItemLog,
  IRectification,
  IProjectTemplate,
  IWorkItemTemplate,
  ICreateTemplateParams,
  ICreateTemplateFromWorkItemsParams,
  IApplyTemplateParams,
  IApplyTemplateResponse,
  IPaginationResponse,
  IApiResponse 
} from '../types';

// API基础路径（相对于api实例的baseURL）
const API_BASE = '/progress';

// ==================== 工程项服务 ====================

export const workItemService = {
  /**
   * 获取工程项列表
   * 
   * @param params.projectId - 项目ID（主要筛选条件）
   */
  getList: async (params?: {
    projectId?: string;
    parentItemId?: string;
    status?: string;
    category?: string;
    isAddition?: boolean;
    tree?: boolean;
    page?: number;
    limit?: number;
  }): Promise<IApiResponse<IPaginationResponse<IWorkItem>>> => {
    const response = await api.get(`${API_BASE}/work-items`, { params });
    return response as any;
  },

  /**
   * 获取工程项树形结构
   * 
   * @param params.projectId - 项目ID（必填）
   */
  getTree: async (params: {
    projectId: string;
  }): Promise<IApiResponse<IWorkItem[]>> => {
    const response = await api.get(`${API_BASE}/work-items/tree`, { params });
    return response as any;
  },

  /**
   * 获取工程项详情
   */
  getById: async (id: string): Promise<IApiResponse<IWorkItem>> => {
    const response = await api.get(`${API_BASE}/work-items/${id}`);
    return response as any;
  },

  /**
   * 创建工程项
   * 
   * @param data.projectId - 项目ID（必填）
   */
  create: async (data: Partial<IWorkItem>): Promise<IApiResponse<IWorkItem>> => {
    const response = await api.post(`${API_BASE}/work-items`, data);
    return response as any;
  },

  /**
   * 批量创建工程项
   * 
   * @param data.projectId - 项目ID（必填）
   */
  batchCreate: async (data: {
    projectId: string;
    items: any[];
  }): Promise<IApiResponse<IWorkItem[]>> => {
    const response = await api.post(`${API_BASE}/work-items/batch`, data);
    return response as any;
  },

  /**
   * 更新工程项
   */
  update: async (id: string, data: Partial<IWorkItem>): Promise<IApiResponse<IWorkItem>> => {
    const response = await api.put(`${API_BASE}/work-items/${id}`, data);
    return response as any;
  },

  /**
   * 更新工程项状态
   */
  updateStatus: async (id: string, status: string): Promise<IApiResponse<IWorkItem>> => {
    const response = await api.patch(`${API_BASE}/work-items/${id}/status`, { status });
    return response as any;
  },

  /**
   * 删除工程项
   */
  delete: async (id: string): Promise<IApiResponse<void>> => {
    const response = await api.delete(`${API_BASE}/work-items/${id}`);
    return response as any;
  }
};

// ==================== 进度记录服务 ====================

export const progressService = {
  /**
   * 获取进度记录列表
   * 
   * @param params.projectId - 项目ID（主要筛选条件）
   */
  getList: async (params?: {
    projectId?: string;
    workItemId?: string;
    startDate?: string;
    endDate?: string;
    recordedBy?: string;
    page?: number;
    limit?: number;
  }): Promise<IApiResponse<IPaginationResponse<IProgressRecord>>> => {
    const response = await api.get(`${API_BASE}/records`, { params });
    return response as any;
  },

  /**
   * 获取工程项进度历史
   */
  getHistory: async (workItemId: string): Promise<IApiResponse<IProgressRecord[]>> => {
    const response = await api.get(`${API_BASE}/records/history/${workItemId}`);
    return response as any;
  },

  /**
   * 获取进度记录详情
   */
  getById: async (id: string): Promise<IApiResponse<IProgressRecord>> => {
    const response = await api.get(`${API_BASE}/records/${id}`);
    return response as any;
  },

  /**
   * 创建进度记录（填报进度）
   * 
   * @param data.workItemId - 工程项ID（必填，通过工程项关联项目）
   */
  create: async (data: {
    workItemId: string;
    recordDate?: string;
    currentQuantity: number;
    description?: string;
    attachments?: any[];
  }): Promise<IApiResponse<IProgressRecord>> => {
    const response = await api.post(`${API_BASE}/records`, data);
    return response as any;
  },

  /**
   * 修改进度记录
   */
  update: async (id: string, data: {
    currentQuantity: number;
    description?: string;
    attachments?: any[];
    modifyReason: string;
  }): Promise<IApiResponse<IProgressRecord>> => {
    const response = await api.put(`${API_BASE}/records/${id}`, data);
    return response as any;
  }
};

// ==================== 增项服务 ====================

export const additionService = {
  getList: async (params?: {
    projectId?: string;
    status?: string;
    reason?: string;
    executorId?: string;
    isSubcontracted?: boolean;
    subcontractorId?: string;
    batchId?: string;
    page?: number;
    limit?: number;
  }): Promise<IApiResponse<IPaginationResponse<IAddition>>> => {
    const response = await api.get(`${API_BASE}/additions`, { params });
    return response as any;
  },

  getStats: async (projectId?: string): Promise<IApiResponse<any>> => {
    const response = await api.get(`${API_BASE}/additions/stats`, { params: { projectId } });
    return response as any;
  },

  getPending: async (): Promise<IApiResponse<IAddition[]>> => {
    const response = await api.get(`${API_BASE}/additions/pending`);
    return response as any;
  },

  getById: async (id: string): Promise<IApiResponse<IAddition>> => {
    const response = await api.get(`${API_BASE}/additions/${id}`);
    return response as any;
  },

  create: async (data: Partial<IAddition> & { projectId: string }): Promise<IApiResponse<IAddition>> => {
    const response = await api.post(`${API_BASE}/additions`, data);
    return response as any;
  },

  batchCreate: async (data: {
    projectId: string;
    entryDate?: string;
    sourceType?: string;
    sourceDescription?: string;
    location?: string;
    items: Array<{
      title: string;
      content?: string;
      category?: string;
      reason?: string;
      executorType?: string;
      executorName?: string;
      isSubcontracted?: boolean;
      subcontractorId?: string;
      subcontractorName?: string;
      subcontractSettlementMethod?: string;
      estimatedAmount?: number;
      plannedStartDate?: string;
      plannedEndDate?: string;
      remark?: string;
    }>;
  }): Promise<IApiResponse<{ batch: any; items: IAddition[] }>> => {
    const response = await api.post(`${API_BASE}/additions/batch`, data);
    return response as any;
  },

  update: async (id: string, data: Partial<IAddition>): Promise<IApiResponse<IAddition>> => {
    const response = await api.put(`${API_BASE}/additions/${id}`, data);
    return response as any;
  },

  updateStatus: async (id: string, data: {
    status: string;
    reason?: string;
    settlementAmount?: number;
    settlementRemark?: string;
  }): Promise<IApiResponse<IAddition>> => {
    const response = await api.patch(`${API_BASE}/additions/${id}/status`, data);
    return response as any;
  },

  addLog: async (id: string, data: {
    progressPercent?: number;
    completedWork?: string;
    actualAmountIncrement?: number;
    issues?: string;
    coordinationNeeded?: string;
    attachments?: any[];
    remark?: string;
  }): Promise<IApiResponse<any>> => {
    const response = await api.post(`${API_BASE}/additions/${id}/log`, data);
    return response as any;
  },

  getLogs: async (id: string): Promise<IApiResponse<any[]>> => {
    const response = await api.get(`${API_BASE}/additions/${id}/logs`);
    return response as any;
  },

  delete: async (id: string, reason?: string): Promise<IApiResponse<void>> => {
    const response = await api.delete(`${API_BASE}/additions/${id}`, { data: { reason } });
    return response as any;
  }
};

// ==================== 整改服务 ====================

export const rectificationService = {
  /**
   * 获取整改列表
   */
  getList: async (params?: {
    projectId?: string;
    workItemId?: string;
    status?: string;
    severity?: string;
    source?: string;
    checkType?: string;
    responsibleUserId?: string;
    page?: number;
    limit?: number;
  }): Promise<IApiResponse<IPaginationResponse<IRectification>>> => {
    const response = await api.get(`${API_BASE}/rectifications`, { params });
    return response as any;
  },

  /**
   * 获取待处理整改列表
   */
  getPending: async (): Promise<IApiResponse<IRectification[]>> => {
    const response = await api.get(`${API_BASE}/rectifications/pending`);
    return response as any;
  },

  /**
   * 获取整改详情
   */
  getById: async (id: string): Promise<IApiResponse<IRectification>> => {
    const response = await api.get(`${API_BASE}/rectifications/${id}`);
    return response as any;
  },

  /**
   * 获取完整流程历史（含打回记录）
   */
  getHistory: async (id: string): Promise<IApiResponse<any[]>> => {
    const response = await api.get(`${API_BASE}/rectifications/${id}/history`);
    return response as any;
  },

  /**
   * 创建整改
   */
  create: async (data: Partial<IRectification>): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications`, data);
    return response as any;
  },

  /**
   * 设置整改期限
   */
  setDeadline: async (id: string, deadline: string): Promise<IApiResponse<IRectification>> => {
    const response = await api.patch(`${API_BASE}/rectifications/${id}/deadline`, { deadline });
    return response as any;
  },

  /**
   * 更新整改进度
   */
  updateProgress: async (id: string, data: { progress: number; remark?: string }): Promise<IApiResponse<IRectification>> => {
    const response = await api.patch(`${API_BASE}/rectifications/${id}/progress`, data);
    return response as any;
  },

  /**
   * 申请延期
   */
  applyExtension: async (id: string, data: { newDeadline: string; reason: string }): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/extensions`, data);
    return response as any;
  },

  /**
   * 填写实际成本
   */
  submitActualCosts: async (id: string, data: {
    laborCost?: number;
    materialCost?: number;
    equipmentCost?: number;
    subcontractCost?: number;
    coordinationCost?: number;
    delayCost?: number;
    costRemark?: string;
  }): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/actual-costs`, data);
    return response as any;
  },

  /**
   * 开始处理整改
   */
  start: async (id: string): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/start`);
    return response as any;
  },

  /**
   * 提交整改完成
   */
  submit: async (id: string, data: {
    comment?: string;
    rectificationAttachments?: any[];
  }): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/submit`, data);
    return response as any;
  },

  /**
   * 总监验收
   */
  directorAccept: async (id: string, data: {
    accepted: boolean;
    comment?: string;
  }): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/director-accept`, data);
    return response as any;
  },

  /**
   * 业主验收
   */
  ownerAccept: async (id: string, data: {
    accepted: boolean;
    comment?: string;
  }): Promise<IApiResponse<IRectification>> => {
    const response = await api.post(`${API_BASE}/rectifications/${id}/owner-accept`, data);
    return response as any;
  },

  /**
   * 删除整改
   */
  delete: async (id: string): Promise<IApiResponse<void>> => {
    const response = await api.delete(`${API_BASE}/rectifications/${id}`);
    return response as any;
  }
};

// ==================== 报表服务 ====================

export const reportService = {
  /**
   * 获取项目进度报表
   * 
   * @param params.projectId - 项目ID（主要筛选条件）
   */
  getProjectProgress: async (params?: {
    projectId?: string;
  }): Promise<IApiResponse<any[]>> => {
    const response = await api.get(`${API_BASE}/reports/project-progress`, { params });
    return response as any;
  },

  /**
   * 获取增项汇总报表
   */
  getAdditionSummary: async (params?: {
    projectId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<IApiResponse<any>> => {
    const response = await api.get(`${API_BASE}/reports/addition-summary`, { params });
    return response as any;
  },

  /**
   * 获取整改统计报表
   */
  getRectificationStats: async (params?: {
    projectId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<IApiResponse<any>> => {
    const response = await api.get(`${API_BASE}/reports/rectification-stats`, { params });
    return response as any;
  },

  /**
   * 导出报表
   */
  exportReport: async (params: {
    type: 'project-progress' | 'addition-summary' | 'rectification-stats';
    projectId?: string;
    startDate?: string;
    endDate?: string;
    format?: string;
  }): Promise<IApiResponse<any>> => {
    const response = await api.get(`${API_BASE}/reports/export`, { params });
    return response as any;
  }
};

// ==================== 项目模板服务 ====================

/**
 * 项目模板服务
 * Project Template Service
 * 
 * 提供模板的CRUD操作、从模板创建工程项、保存为模板等功能
 */
export const templateService = {
  /**
   * 获取模板列表
   * Get template list
   * 
   * @param params - 查询参数
   * @param params.type - 模板类型 (forestry/civil/custom)
   * @param params.isSystem - 是否系统预设
   * @param params.search - 搜索关键词
   * @param params.page - 页码
   * @param params.limit - 每页数量
   */
  getList: async (params?: {
    type?: string;
    isSystem?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<IApiResponse<IPaginationResponse<IProjectTemplate>>> => {
    const response = await api.get(`${API_BASE}/templates`, { params });
    return response as any;
  },

  /**
   * 获取系统预设模板
   * Get system preset templates
   */
  getSystemTemplates: async (): Promise<IApiResponse<IProjectTemplate[]>> => {
    const response = await api.get(`${API_BASE}/templates/system`);
    return response as any;
  },

  /**
   * 获取热门模板
   * Get popular templates
   * 
   * @param limit - 返回数量限制
   */
  getPopularTemplates: async (limit?: number): Promise<IApiResponse<IProjectTemplate[]>> => {
    const response = await api.get(`${API_BASE}/templates/popular`, { params: { limit } });
    return response as any;
  },

  /**
   * 获取模板详情
   * Get template detail
   * 
   * @param id - 模板ID
   */
  getById: async (id: string): Promise<IApiResponse<IProjectTemplate>> => {
    const response = await api.get(`${API_BASE}/templates/${id}`);
    return response as any;
  },

  /**
   * 获取模板的树形结构工程项
   * Get template work items in tree structure
   * 
   * @param id - 模板ID
   */
  getTree: async (id: string): Promise<IApiResponse<IWorkItemTemplate[]>> => {
    const response = await api.get(`${API_BASE}/templates/${id}/tree`);
    return response as any;
  },

  /**
   * 创建模板
   * Create template
   * 
   * @param data - 模板数据
   */
  create: async (data: ICreateTemplateParams): Promise<IApiResponse<IProjectTemplate>> => {
    const response = await api.post(`${API_BASE}/templates`, data);
    return response as any;
  },

  /**
   * 从现有工程项创建模板（保存为模板）
   * Create template from existing work items (Save as template)
   * 
   * @param data - 创建参数
   * @param data.name - 模板名称
   * @param data.description - 模板描述
   * @param data.projectId - 项目ID（从项目的所有工程项创建）
   * @param data.workItemIds - 工程项ID列表（从指定工程项创建）
   */
  createFromWorkItems: async (data: ICreateTemplateFromWorkItemsParams): Promise<IApiResponse<IProjectTemplate>> => {
    const response = await api.post(`${API_BASE}/templates/from-work-items`, data);
    return response as any;
  },

  /**
   * 应用模板创建工程项
   * Apply template to create work items
   * 
   * @param id - 模板ID
   * @param data - 应用参数
   * @param data.projectId - 目标项目ID（必填）
   * @param data.selectedItems - 选中的模板项ID列表（可选，不传则全部创建）
   * @param data.quantityOverrides - 数量覆盖 { templateItemId: quantity }
   * @param data.priceOverrides - 价格覆盖 { templateItemId: price }
   */
  apply: async (id: string, data: IApplyTemplateParams): Promise<IApiResponse<IApplyTemplateResponse>> => {
    const response = await api.post(`${API_BASE}/templates/${id}/apply`, data);
    return response as any;
  },

  /**
   * 更新模板
   * Update template
   * 
   * @param id - 模板ID
   * @param data - 更新数据
   */
  update: async (id: string, data: Partial<ICreateTemplateParams>): Promise<IApiResponse<IProjectTemplate>> => {
    const response = await api.put(`${API_BASE}/templates/${id}`, data);
    return response as any;
  },

  /**
   * 删除模板
   * Delete template
   * 
   * @param id - 模板ID
   */
  delete: async (id: string): Promise<IApiResponse<void>> => {
    const response = await api.delete(`${API_BASE}/templates/${id}`);
    return response as any;
  }
};
